import type * as THREE from 'three'
import { getLoggerManager } from '@/shared/logging'
import type { Disposable } from '@/shared/types/core'
import type { Layer } from './Layer'

const log = getLoggerManager().getLogger('gsplat:lod')

/**
 * 相机组件（简化版 - 仅包含 Layer 过滤）
 *
 * @remarks
 * 对应 PlayCanvas 的 CameraComponent 的 Layer 管理部分
 */
export class CameraComponent {
  /**
   * Three.js 相机实例
   */
  camera: THREE.Camera

  /**
   * 此相机渲染的 Layer ID 列表
   */
  layers: number[] = []

  /**
   * Layer ID 集合（用于快速查找）
   * @internal
   */
  layersSet: Set<number> = new Set()

  constructor(camera: THREE.Camera, layers: number[] = []) {
    this.camera = camera
    this.setLayers(layers)
  }

  /**
   * 设置相机渲染的 Layer ID 列表。
   *
   * @param layers - Layer ID 数组，内部会克隆
   */
  setLayers(layers: number[]): void {
    this.layers = layers.slice()
    this.layersSet = new Set(layers)
  }

  /**
   * 添加 Layer ID。
   *
   * @param layerId - 待追加的 Layer ID；已存在时忽略
   */
  addLayer(layerId: number): void {
    if (!this.layersSet.has(layerId)) {
      this.layers.push(layerId)
      this.layersSet.add(layerId)
    }
  }

  /**
   * 移除 Layer ID。
   *
   * @param layerId - 待移除的 Layer ID；不存在时忽略
   */
  removeLayer(layerId: number): void {
    if (this.layersSet.has(layerId)) {
      const index = this.layers.indexOf(layerId)
      if (index !== -1) {
        this.layers.splice(index, 1)
      }
      this.layersSet.delete(layerId)
    }
  }
}

/**
 * LayerComposition - 简化的 Layer 组合系统
 *
 * @remarks
 * 对应 PlayCanvas 的 LayerComposition 类（简化版）
 * 移除了复杂的渲染管线支持，仅保留 Layer 容器和相机管理
 *
 * 关键差异：
 * - 移除渲染顺序管理（subLayerList、opaqueSortMode 等）
 * - 移除光照和阴影支持
 * - 简化为 Layer 数组容器和相机集合
 */
export type LayerCompositionChange<TPlacement extends object, TDrawObject extends object> =
  | {
      readonly type: 'added'
      readonly layer: Layer<TPlacement, TDrawObject>
    }
  | {
      readonly type: 'removed'
      readonly layer: Layer<TPlacement, TDrawObject>
    }

export class LayerComposition<
  TPlacement extends object = import('./GSplatPlacement').GSplatPlacement,
  TDrawObject extends object = object,
> {
  /**
   * Layer 列表（按添加顺序）
   */
  layerList: Layer<TPlacement, TDrawObject>[] = []

  /**
   * Layer ID 到 Layer 的映射
   * @internal
   */
  private layerMap = new Map<number, Layer<TPlacement, TDrawObject>>()

  /**
   * Layer 名称到 Layer 的映射
   * @internal
   */
  private layerNameMap = new Map<string, Layer<TPlacement, TDrawObject>>()

  /** Component/Handle binding 精确持有的 Layer topology listener leases。 */
  private readonly layerChangeListeners = new Set<
    (change: LayerCompositionChange<TPlacement, TDrawObject>) => void
  >()

  /**
   * 相机组件列表
   */
  cameras: CameraComponent[] = []

  /**
   * 相机集合（用于快速查找）
   * @internal
   */
  camerasSet: Set<THREE.Camera> = new Set()

  // ==================== Layer 管理 ====================

  /**
   * 添加 Layer 到组合
   *
   * @param layer - Layer 实例
   */
  addLayer(layer: Layer<TPlacement, TDrawObject>): void {
    if (this.layerMap.has(layer.id)) {
      log.warn(`Layer with id ${layer.id} already exists`)
      return
    }

    this.layerList.push(layer)
    this.layerMap.set(layer.id, layer)
    this.layerNameMap.set(layer.name, layer)
    this.emitLayerChange({ type: 'added', layer })
  }

  /**
   * 移除 Layer
   *
   * @param layer - Layer 实例
   */
  removeLayer(layer: Layer<TPlacement, TDrawObject>): void {
    const index = this.layerList.indexOf(layer)
    if (index !== -1) {
      this.layerList.splice(index, 1)
      this.layerMap.delete(layer.id)
      this.layerNameMap.delete(layer.name)
      for (const cameraComponent of this.cameras) {
        cameraComponent.removeLayer(layer.id)
      }
      this.emitLayerChange({ type: 'removed', layer })
    }
  }

  /**
   * 通过 ID 查找 Layer
   *
   * @param id - Layer ID
   * @returns Layer 实例或 null
   */
  getLayerById(id: number): Layer<TPlacement, TDrawObject> | null {
    return this.layerMap.get(id) ?? null
  }

  /**
   * 通过名称查找 Layer
   *
   * @param name - Layer 名称
   * @returns Layer 实例或 null
   */
  getLayerByName(name: string): Layer<TPlacement, TDrawObject> | null {
    return this.layerNameMap.get(name) ?? null
  }

  // ==================== 相机管理 ====================

  /**
   * 添加相机组件
   *
   * @param cameraComponent - CameraComponent 实例
   */
  addCamera(cameraComponent: CameraComponent): void {
    if (!this.camerasSet.has(cameraComponent.camera)) {
      this.cameras.push(cameraComponent)
      this.camerasSet.add(cameraComponent.camera)
    }
  }

  /**
   * 移除相机组件
   *
   * @param cameraComponent - CameraComponent 实例
   */
  removeCamera(cameraComponent: CameraComponent): void {
    const index = this.cameras.indexOf(cameraComponent)
    if (index !== -1) {
      this.cameras.splice(index, 1)
      this.camerasSet.delete(cameraComponent.camera)
    }
  }

  /**
   * 通过 Three.js Camera 实例查找 CameraComponent。
   *
   * @param camera - 目标相机
   * @returns 绑定到该相机的 CameraComponent；未找到返回 null
   */
  getCameraComponent(camera: THREE.Camera): CameraComponent | null {
    return this.cameras.find((c) => c.camera === camera) ?? null
  }

  // ==================== 工具方法 ====================

  /**
   * 清空所有 Layer
   */
  clearLayers(): void {
    const layers = [...this.layerList]
    const layerIds = layers.map((layer) => layer.id)
    for (const cameraComponent of this.cameras) {
      for (const layerId of layerIds) {
        cameraComponent.removeLayer(layerId)
      }
    }
    this.layerList.length = 0
    this.layerMap.clear()
    this.layerNameMap.clear()
    for (const layer of layers) this.emitLayerChange({ type: 'removed', layer })
  }

  /**
   * 清空所有相机
   */
  clearCameras(): void {
    this.cameras.length = 0
    this.camerasSet.clear()
  }

  /** 只解除当前订阅者，不按事件名批量清理其它 owner。 */
  subscribeLayerChanges(
    listener: (change: LayerCompositionChange<TPlacement, TDrawObject>) => void
  ): Disposable {
    this.layerChangeListeners.add(listener)
    let disposed = false
    return {
      dispose: () => {
        if (disposed) return
        disposed = true
        this.layerChangeListeners.delete(listener)
      },
    }
  }

  private emitLayerChange(change: LayerCompositionChange<TPlacement, TDrawObject>): void {
    for (const listener of [...this.layerChangeListeners]) listener(change)
  }
}
